import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Link2, Share2, Check } from "lucide-react";

/*
 * Reusable Lightbox, opens any image in a full-screen overlay with next/prev
 * navigation, keyboard shortcuts (Esc, ← / →) and optional Copy-Link + Share.
 *
 * Usage:
 *   const { open, LightboxNode } = useLightbox(items);   // items: [{ src, alt, id? }]
 *   <img onClick={() => open(index)} />
 *   {LightboxNode}
 */
export const useLightbox = (items) => {
    const [i, setI] = useState(null);
    const open  = useCallback((idx) => setI(idx), []);
    const close = useCallback(() => setI(null), []);
    const prev  = useCallback(() => setI((n) => (n == null ? null : (n - 1 + items.length) % items.length)), [items.length]);
    const next  = useCallback(() => setI((n) => (n == null ? null : (n + 1) % items.length)), [items.length]);

    useEffect(() => {
        if (i == null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft")  prev();
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [i, close, prev, next]);

    const LightboxNode = i == null ? null : createPortal(
        <div
            role="dialog"
            aria-modal="true"
            data-testid="site-lightbox"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: "rgba(20, 8, 10, 0.95)" }}
            onClick={close}
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); close(); }}
                aria-label="Close"
                data-testid="lightbox-close"
                className="absolute top-4 right-4 rounded-full bg-black/50 hover:bg-black/80 text-cream-50 p-2 border border-gold-300/60"
            >
                <X size={22} />
            </button>
            {items.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        aria-label="Previous"
                        data-testid="lightbox-prev"
                        className="absolute left-3 sm:left-6 rounded-full bg-black/50 hover:bg-black/80 text-cream-50 p-2 border border-gold-300/60"
                    >
                        <ChevronLeft size={26} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        aria-label="Next"
                        data-testid="lightbox-next"
                        className="absolute right-3 sm:right-6 rounded-full bg-black/50 hover:bg-black/80 text-cream-50 p-2 border border-gold-300/60"
                    >
                        <ChevronRight size={26} />
                    </button>
                </>
            )}
            <figure className="max-h-[92vh] max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img
                    src={items[i].src}
                    alt={items[i].alt || ""}
                    className="max-h-[82vh] max-w-full rounded-2xl object-contain"
                    style={{ boxShadow: "0 40px 90px -30px rgba(154, 32, 38, 0.75)" }}
                    loading="eager"
                    decoding="async"
                />
                {(items[i].alt || items.length > 1) && (
                    <figcaption className="mt-3 max-w-2xl text-center text-sm sm:text-base font-serif italic px-4" style={{ color: "#F3EDE4" }}>
                        {items[i].alt && <span>{items[i].alt} </span>}
                        {items.length > 1 && <span className="text-xs opacity-70">{i + 1} / {items.length}</span>}
                    </figcaption>
                )}
            </figure>
        </div>,
        document.body
    );

    return { open, close, LightboxNode, isOpen: i != null, index: i };
};

/*
 * ClickableImage, thin wrapper that turns any <img> into a lightbox trigger.
 * Pass either `items` (an array of {src,alt}) plus an `index`, or a solo image via `src` + `alt`.
 * If a `src` is given without items, the lightbox becomes a single-image viewer.
 */
export const ClickableImage = ({ src, alt = "", className = "", items, index, onClick, ...rest }) => {
    const [soloOpen, setSoloOpen] = useState(false);

    const handleClick = (e) => {
        if (onClick) return onClick(e);
        setSoloOpen(true);
    };

    return (
        <>
            <img
                src={src}
                alt={alt}
                loading="lazy"
                decoding="async"
                onClick={handleClick}
                className={`${className} cursor-zoom-in`}
                {...rest}
            />
            {soloOpen && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="site-lightbox"
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                    style={{ background: "rgba(20, 8, 10, 0.95)" }}
                    onClick={() => setSoloOpen(false)}
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSoloOpen(false); }}
                        aria-label="Close"
                        data-testid="lightbox-close"
                        className="absolute top-4 right-4 rounded-full bg-black/50 hover:bg-black/80 text-cream-50 p-2 border border-gold-300/60"
                    >
                        <X size={22} />
                    </button>
                    <figure className="max-h-[92vh] max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={src}
                            alt={alt}
                            className="max-h-[82vh] max-w-full rounded-2xl object-contain"
                            style={{ boxShadow: "0 40px 90px -30px rgba(154, 32, 38, 0.75)" }}
                        />
                        {alt && (
                            <figcaption className="mt-3 max-w-2xl text-center text-sm sm:text-base font-serif italic px-4" style={{ color: "#F3EDE4" }}>
                                {alt}
                            </figcaption>
                        )}
                    </figure>
                </div>,
                document.body
            )}
        </>
    );
};

export default useLightbox;
